import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';

import {
  CoreLink,
  setOperationStatus,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  userRegisterWithPassword,
  userGetCurrent,
  UI_OPERATION_STATUS_SUCCESS,
} from '@uncinc/react-drupal-core';


import {
  FormikWrapper,
  FormTextField,
  FormCheckbox,
  Button,
  Container,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';

import { formatCartItemsForAnalytics } from '../../../helpers/commerce';
import {
  clearPageInformation,
  setPageInformation,
  setPageViewSent,
  getNewsletterContact,
  getNewsletterContactPropertyOptions,
  createOrUpdateNewsletterContactSubscription,
} from '../../../store/actions/UI';
import { createOrUpdateUserProfile } from '../../../store/actions/Profile';
import { ROUTE_ACCOUNT_EDIT, ROUTE_ACCOUNT_LOGIN, ROUTE_SUCCESS_REGISTER } from '../../../config/routes';

import { prefixObjectKeys, prepareSpotlerListData } from '../../../helpers';
import { ProfileForm, getProfileValidationSchema } from '../../../components/ProfileForm';
import InformationOverlay from '../../../components/InformationOverlay';

import LogoImageVNL from '../../../assets/images/VNL_logo_clean.svg';
import './style.scss';
import {
  DEFAULT_LANGUAGE,
  MINIMUM_PASSWORD_CHARACTER_COUNT,
  URL_SSO_FLOW_IDENTIFIER,
  OTHER_FORM_ITEM_ID,
  SPOTLER_PERMISSIONS_BITS_WHITELIST,
  SPOTLER_PERMISSIONS_HIDDEN_DEFAULT_BITS,
  SPOTLER_SOURCE_REGISTRATION,
  APP_OPTIONS,
  APPLICATON_VNL_ID,
  SSO_APPLICATON_RISK_MONITOR_ID,
} from '../../../config/constants';

const UserRegister = () => {
  const PAGE_TITLE = 'Registreer | VeiligheidNL';
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const history = useHistory();
  const [submittedFormValues, setSubmittedFormValues] = useState();
  const loggedIn = useSelector((state) => state.getIn(['login', 'loggedIn'], false));
  const userRegisterStatus = useSelector((state) => state.getIn(['ui', 'user', 'operations', 'register', 'status']));
  const profileUpdateStatus = useSelector((state) => state.getIn(['ui', 'profile', 'operations', 'update', 'status']));
  const totalPrice = useSelector((state) => state.getIn(['commerce', 'cart', 'content', 'attributes', 'order_total', 'subtotal', 'number']));
  const analyticsItems = useSelector((state) => formatCartItemsForAnalytics(state.getIn(['commerce', 'cart', 'products'])));

  // Spotler subscription data.
  const contactPermissionOptions = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact_property_options', 'permissions'], null)));
  const spotlerUserContact = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact'], undefined)));
  const subscriptionSaveStatus = useSelector((state) => state.getIn(['ui', 'newsletter', 'operations', 'update', 'status']));

  const spotlerWorkFieldList = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact_property_options', 'list1'], undefined)));
  const spotlerJobTitleList = useSelector((state) => (state.getIn(['uiInternal', 'newsletter', 'contact_property_options', 'list2'], undefined)));

  const { t } = useTranslation();
  const urlParams = new URLSearchParams(window.location.search);
  const redirectUrl = urlParams.get('destination');
  const entryNid = urlParams.get('nid');
  const isLoginWallRegister = urlParams.get('source') === 'login_wall';

  const userParamValues = Object.fromEntries(urlParams);
  const isSSORegister = urlParams.get(URL_SSO_FLOW_IDENTIFIER) !== null;
  let activeApplication = APPLICATON_VNL_ID;
  if (APP_OPTIONS.SsoRiskMonitorClientIds.includes(urlParams.get('client_id'))) {
    activeApplication = SSO_APPLICATON_RISK_MONITOR_ID;
  }

  useEffect(() => {
    dispatch(userGetCurrent());
    dispatch(clearPageInformation());
    dispatch(setPageInformation(PAGE_TITLE));
    dispatch(setPageViewSent(false));
    dispatch(getNewsletterContactPropertyOptions());
  }, []);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t('form.register.email.invalid'))
      .required(t('form.register.email.required')),
    passwordRegister: Yup.string()
      .min(MINIMUM_PASSWORD_CHARACTER_COUNT, t('form.register.password.minimum', { count: MINIMUM_PASSWORD_CHARACTER_COUNT }))
      .matches(/^(?=.*\d)(?=.*[a-zA-Z]).*$/, t('form.register.password.invalid'))
      .required(t('form.register.password.required')),
    passwordRegisterConfirm: Yup.string()
      .oneOf([Yup.ref('passwordRegister'), null], t('form.register.password.no_match'))
      .required(t('form.register.password.required')),
    // Load in profile fields.
    ...getProfileValidationSchema(),
    privacy_terms: Yup.boolean().required(t('form.register.privacy_required')),
  });

  const onSubmit = (values, form) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    if (totalPrice > 0) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'add_shipping_info',
        ecommerce: {
          currency: 'EUR',
          value: totalPrice,
          items: analyticsItems,
        },
      });
    }

    setSubmittedFormValues(values);
    // Register the user.
    dispatch(userRegisterWithPassword(values.email, values.email, values.passwordRegister));
    form.setSubmitting(false);
  };

  useEffect(() => {
    if (loggedIn === true && !submittedFormValues) {
      if (redirectUrl) {
        window.location = `${redirectUrl}${window.location.search}`;
      } else {
        history.push(ROUTE_ACCOUNT_EDIT);
      }
    }
  }, [history, dispatch, loggedIn]);

  useEffect(() => {
    if (loggedIn === true) {
      dispatch(setOperationStatus('user', 'register', null));
      if (submittedFormValues) {
        // Save our profile information.
        const profileFormData = Object.fromEntries(
          Object.entries(submittedFormValues).filter(([key]) => key.startsWith('field_') && !key.endsWith('_other')),
        );

        if (profileFormData.field_work_area === OTHER_FORM_ITEM_ID) {
          profileFormData.field_work_area = submittedFormValues.field_work_area_other;
        }

        if (profileFormData.field_job_title === OTHER_FORM_ITEM_ID) {
          profileFormData.field_job_title = submittedFormValues.field_job_title_other;
        }

        // Pass the entry Node to the profile.
        if (entryNid) {
          profileFormData.field_entry_node = [{ target_id: Number(entryNid) }];
        }

        // Address is required, add an empty one.
        profileFormData.address = [{
          country_code: 'NL',
        }];
        dispatch(createOrUpdateUserProfile(profileFormData));

        let eventName = 'register_success_regular';

        if (isLoginWallRegister) {
          eventName = 'register_success_login_wall';
        }

        if (isSSORegister) {
          eventName = 'register_success_risk_monitor';
        }

        // Push custom GTM event
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: eventName,
        });
      }
    }
  }, [history, dispatch, loggedIn, redirectUrl, submittedFormValues]);

  useEffect(() => {
    if (loggedIn === true && profileUpdateStatus === UI_OPERATION_STATUS_SUCCESS) {
      dispatch(setOperationStatus('profile', 'update', null));
      // Try to fetch the Spotler contact with the given email address.
      dispatch(getNewsletterContact(submittedFormValues.email));
    }
  }, [dispatch, profileUpdateStatus, loggedIn, redirectUrl]);

  useEffect(() => {
    // If the Spotler contact was loaded and we have submitted the form
    // TODO: https://uncinc.monday.com/boards/1674548856/pulses/8560148204
    if (spotlerUserContact !== undefined && submittedFormValues) {
      // Prepare Spotler data.
      const spotlerListData = prepareSpotlerListData(
        {
          fields: spotlerWorkFieldList,
          selected: submittedFormValues.field_work_area === OTHER_FORM_ITEM_ID
            ? submittedFormValues.field_work_area_other : submittedFormValues.field_work_area,
          selected_other: submittedFormValues.field_work_area_other,
        },
        {
          fields: spotlerJobTitleList,
          selected: submittedFormValues.field_job_title === OTHER_FORM_ITEM_ID
            ? submittedFormValues.field_job_title_other : submittedFormValues.field_job_title,
          selected_other: submittedFormValues.field_job_title_other,
        },
      );
      // Assemble values.
      const spotlerValues = {
        source: SPOTLER_SOURCE_REGISTRATION,
        email: submittedFormValues.email,
        firstName: submittedFormValues.field_first_name,
        lastName: submittedFormValues.field_infix_name ? `${submittedFormValues.field_infix_name} ${submittedFormValues.field_last_name}` : submittedFormValues.field_last_name,
        organisation: submittedFormValues.field_organisation_name,
        ...spotlerListData,
      };

      // Add all the permissions if we don't have a spotler account yet
      if (spotlerUserContact === null) {
        const newContactPermissions = contactPermissionOptions.map((contactPropertyPermissionOption) => (
          {
            ...contactPropertyPermissionOption,
            enabled: SPOTLER_PERMISSIONS_BITS_WHITELIST.includes(contactPropertyPermissionOption.bit) || SPOTLER_PERMISSIONS_HIDDEN_DEFAULT_BITS.includes(contactPropertyPermissionOption.bit),
          }
        ));
        spotlerValues.permissions = newContactPermissions;
      }

      // Create or update our Spotler newsletter subscription.
      dispatch(createOrUpdateNewsletterContactSubscription(spotlerValues));
    }
  }, [spotlerUserContact, submittedFormValues]);

  // React to the newsletter subscription save.
  useEffect(() => {
    if (subscriptionSaveStatus === UI_OPERATION_STATUS_SUCCESS) {
      const targeturl = redirectUrl || ROUTE_SUCCESS_REGISTER;
      // Redirect.
      if (redirectUrl && redirectUrl !== ROUTE_ACCOUNT_EDIT && redirectUrl !== ROUTE_SUCCESS_REGISTER) {
        window.location = `${redirectUrl}${window.location.search}`;
      } else {
        history.push(targeturl);
      }
    }
    dispatch(setOperationStatus('newsletter', 'update', null, null));
  }, [history, redirectUrl, subscriptionSaveStatus, dispatch]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: DEFAULT_LANGUAGE }}>
        <title>{PAGE_TITLE}</title>
      </Helmet>
      <Container small>
        <div className="FormPage PageRegister PageMinimal" id="main">
          <div className="PageMinimal__top">
            <CoreLink className="Logo" href="/">
              <img src={LogoImageVNL} alt="VeiligheidNL" />
            </CoreLink>
            <h1>{t(`form.register.${activeApplication}_title`)}</h1>
          </div>
          <div className="PageMinimal__content">
            {(userRegisterStatus === UI_OPERATION_STATUS_ERROR) && (
              <div className="Form__error">
                {t('form.register.email.exists')}
                <CoreLink href={ROUTE_ACCOUNT_LOGIN} className="Form__error__link">
                  {t('form.register.email.exists_login_link')}
                </CoreLink>
              </div>
            )}
            <FormikWrapper
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              initialValues={prefixObjectKeys('field_', userParamValues, [
                'first_name', 'last_name', 'infix_name',
              ])}
              key={Object.keys(userParamValues).length}
              innerRef={formRef}
            >

              {activeApplication === SSO_APPLICATON_RISK_MONITOR_ID && (
                <div className="FormElement TextField OverlayFormElement">
                  <label htmlFor="email">{t('login.email.label')}</label>
                  <InformationOverlay
                    overlayKey="register_risk_monitor_email_info_overlay"
                    title={t('form.register_risk_monitor.overlay_title')}
                    body={t('form.register_risk_monitor.overlay_body')}
                  />
                </div>
              )}

              <FormTextField
                title={activeApplication !== SSO_APPLICATON_RISK_MONITOR_ID ? t('login.email.label') : ''}
                fieldName="email"
                type="email"
                disabled={userParamValues.email && isSSORegister}
                required
                data-lpignore
                autocomplete="off"
              />

              {/* Load in the profile form fields */}
              <ProfileForm
                formRef={formRef}
              />

              <FormTextField
                title={t('login.password.label')}
                fieldName="passwordRegister"
                type="password"
                className="InputPassword"
                showPasswordReveal
                required
                data-lpignore
                autocomplete="off"
              />
              <FormTextField
                title={t('form.register.password.confirm')}
                fieldName="passwordRegisterConfirm"
                type="password"
                className="InputPassword"
                showPasswordReveal
                required
                data-lpignore
                autocomplete="off"
              />
              <WysiwygField
                property="schema:text"
                html={t('form.register.privacy_text')}
              />

              <div className="WebformCheckboxes privacy">
                <FormCheckbox
                  title={t('form.register.privacy_label')}
                  fieldName="privacy_terms"
                  required
                />
              </div>

              <div className="Form__actions">
                <Button
                  type="submit"
                  onClick={() => {
                    // Temp fix for touching the select fields to show error.
                    // https://uncinc.monday.com/boards/1674548856/pulses/9431098326
                    formRef?.current?.setFieldTouched('field_work_area');
                    formRef?.current?.setFieldTouched('field_job_title');
                  }}
                  className={`Button Button--primary ${(userRegisterStatus === UI_OPERATION_STATUS_REQUEST) ? 'Button--loading' : ''}`}
                >
                  {t('form.register.button')}
                  <i className="icon icon-arrow-right" />
                </Button>
                {activeApplication === APPLICATON_VNL_ID && (
                  <a href={`/login${window.location.search}`} className="LoginButton">
                    <span>{t('login.register_button')}</span>
                  </a>
                )}
              </div>
            </FormikWrapper>
          </div>
          <div className="PageMinimal__usp">
            <div className="PageMinimal__usp__link">
              <CoreLink href="/klantenservice" target="_blank" className="Button Button--secondary">
                {t('general.need_help')}
              </CoreLink>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default UserRegister;
